import { motion } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, Code2, Smartphone, Rocket, Paintbrush, Zap, Wrench, Layers, Workflow, Building2 } from "lucide-react";
import { useListServices } from "@workspace/api-client";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

const iconMap: Record<string, any> = {
  'website-development': Code2,
  'mobile-app-development': Smartphone,
  'mvp-launch': Rocket,
  'ui-ux-redesign': Paintbrush,
  'speed-optimisation': Zap,
  'maintenance-support': Wrench,
  'feature-scaling': Layers,
  'automation-tools': Workflow,
  'technical-consulting': Building2
};

export default function Services() {
  const { data: servicesData, isLoading } = useListServices();

  const services = servicesData && servicesData.length > 0 ? servicesData : [];

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="Our Services | Elite Engineering Power"
        description="Everything you need to build, launch, and scale digital products at maximum velocity. From web development to MVP launch and technical consulting."
      />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "AccelerateByte Services",
        "url": "https://acceleratebyte.com/services",
        "description": "Everything you need to build, launch, and scale digital products at maximum velocity.",
        "itemListElement": services.map((srv: any, i: number) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "Service",
            "name": srv.title,
            "url": `https://acceleratebyte.com/services/${srv.slug}`,
            "description": srv.description,
            "provider": { "@id": "https://acceleratebyte.com/#organization" }
          }
        }))
      }} />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What software development services does AccelerateByte offer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "AccelerateByte offers nine core services: website development, mobile app development, MVP launch, UI/UX redesign, speed optimisation, maintenance and support, feature scaling, automation tools, and technical consulting."
            }
          },
          {
            "@type": "Question",
            "name": "Can AccelerateByte build both iOS and Android apps?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. AccelerateByte builds cross-platform iOS and Android apps using React Native, delivering native performance on both platforms from a single codebase — reducing cost and development time."
            }
          },
          {
            "@type": "Question",
            "name": "How quickly can AccelerateByte build an MVP?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "AccelerateByte can deliver a production-ready, investor-ready MVP in 2-6 weeks depending on complexity. Our rapid delivery process is designed specifically for founders and startups that need to move fast."
            }
          },
          {
            "@type": "Question",
            "name": "Does AccelerateByte offer ongoing website maintenance?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. AccelerateByte offers dedicated maintenance and support packages including bug fixes, performance monitoring, security updates, and incremental feature additions to keep your product running smoothly."
            }
          },
          {
            "@type": "Question",
            "name": "Can AccelerateByte improve an existing product?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. AccelerateByte offers UI/UX redesign, speed optimisation (Core Web Vitals), feature scaling, and technical consulting for existing products that need modernisation, better performance, or new capabilities."
            }
          },
          {
            "@type": "Question",
            "name": "Does AccelerateByte build Next.js websites?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. AccelerateByte builds fast, SEO-optimised websites and web applications using React and Next.js, including server-side rendering, static generation, and modern full-stack architectures."
            }
          }
        ]
      }} />
      {/* HEADER */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 tracking-tight">Our Services</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to build, launch, and scale digital products at maximum velocity.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-12 px-4 container mx-auto mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((srv: any, i: number) => {
            const Icon = iconMap[srv.slug] || Code2;
            return (
              <Link key={i} href={`/services/${srv.slug}`}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ delay: (i % 3) * 0.1 }} className="group h-full rounded-2xl border border-border/50 bg-card p-8 transition-all hover:border-primary/50 hover:bg-card/80 cursor-pointer relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-6 opacity-0 translate-x-4 -translate-y-4 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all text-primary"><ArrowRight className="h-6 w-6" /></div>
                  <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all"><Icon className="h-7 w-7" /></div>
                  <h3 className="font-display text-xl font-bold mb-3">{srv.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{srv.description}</p>
                </motion.div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-32 px-4 relative overflow-hidden mt-auto border-t border-primary/20">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-background to-purple-900/40"></div>
        <div className="container relative z-10 mx-auto text-center max-w-3xl">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-white">Not sure what you need?</h2>
          <p className="text-lg text-blue-100/80 mb-10">Schedule a free technical consulting call. We'll analyze your business goals and recommend the exact technical path forward.</p>
          <Link href="/contact" className="inline-flex h-14 items-center justify-center rounded-md bg-white text-blue-950 px-8 text-lg font-bold shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all hover:scale-105">
            Book a Discovery Call
          </Link>
        </div>
      </section>
    </div>
  );
}
